// Assembles a flat "sound library" catalog from ALL uploaded kits.
//
// v19.7 — Converted to a subscribable module store. The prior version
// exposed only `getKitSoundLibrary()` (fetch-on-demand); every Sampler
// mount fetched independently, and Training-Ground pulses required each
// live Sampler instance to remember to re-fetch itself. In practice
// (see /app/test_reports/iteration_46 v19.7) the effect-driven refetch
// path was intermittently losing its `setKitSounds` update — the DAW
// dropdown stayed empty (or stale) until the producer clicked the
// manual refresh button. Root-cause: nothing coordinated the fetch
// result with the Sampler's local `kitSounds` state across strict-mode
// remounts and post-`setTrackSample` re-renders.
//
// The new shape:
//   • A single module-level `_snapshot` holds the freshest catalog.
//   • `refreshKitSoundLibrary()` fetches + updates `_snapshot` + fans
//     out to every subscriber.
//   • `subscribeKitSoundLibrary(fn)` returns an unsubscribe.
//   • The kitsBus pulse (Training-Ground CRUD) triggers a refresh
//     centrally, so subscribers just consume the snapshot.
//   • `getKitSoundLibrary()` is preserved for one-shot callers (e.g.
//     the DAWPage-level reconcile effect); it now returns the current
//     snapshot after ensuring at least one fetch has happened.
import drumKitManager from "./drumKitManager";
import bassKitManager from "./bassKitManager";
import melodyKitManager from "./melodyKitManager";
import { onKitsChanged } from "../utils/kitsBus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Human-friendly labels for drum / melody slots.
const DRUM_LABELS = {
  kick: "Kick", snare: "Snare", hihat: "Hi-Hat", openhat: "Open Hat",
  clap: "Clap", perc: "Percussion", ride: "Ride", tom: "Tom",
};
const MELODY_LABELS = {
  piano: "Piano", lead: "Lead Synth", brass: "Brass", strings: "Strings", flute: "Flute",
};

function toAbsUrl(u) {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("blob:")) return u;
  return `${BACKEND_URL}${u.startsWith("/") ? u : `/${u}`}`;
}

async function fetchAllKits(endpoint) {
  try {
    const res = await fetch(`${API}/training/${endpoint}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.kits) ? data.kits : [];
  } catch { return []; }
}

function buildCatalog(drumKits, bassKits, melodyKits) {
  const sounds = [];
  const drumSorted = [...drumKits].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
  drumSorted.forEach((k) => {
    const samples = k.samples || {}; const labels = k.labels || {};
    Object.entries(samples).forEach(([slot, meta]) => {
      if (!meta?.url) return;
      const custom = (labels[slot] || meta.display_name || meta.name || "").trim();
      sounds.push({
        id: `drum:${k.id}:${slot}`,
        label: custom || `${DRUM_LABELS[slot] || slot} · ${k.name || "Kit"}`,
        url: toAbsUrl(meta.url),
        kit: "drum", kitId: k.id, kitName: k.name, slot,
        rootPitch: "C4", oneShot: true,
      });
    });
  });
  const bassSorted = [...bassKits].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
  bassSorted.forEach((k) => {
    const samples = k.samples || {}; const labels = k.labels || {};
    Object.entries(samples).forEach(([pitch, meta]) => {
      if (!meta?.url) return;
      const custom = (labels[pitch] || meta.display_name || meta.name || "").trim();
      sounds.push({
        id: `bass:${k.id}:${pitch}`,
        label: custom || `808 ${pitch} · ${k.name || "Kit"}`,
        url: toAbsUrl(meta.url),
        kit: "bass", kitId: k.id, kitName: k.name, slot: pitch,
        rootPitch: pitch, oneShot: false,
      });
    });
  });
  const melodySorted = [...melodyKits].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
  melodySorted.forEach((k) => {
    const samples = k.samples || {}; const labels = k.labels || {};
    const root = k.root_pitch || "C4";
    Object.entries(samples).forEach(([slot, meta]) => {
      if (!meta?.url) return;
      const custom = (labels[slot] || meta.display_name || meta.name || "").trim();
      sounds.push({
        id: `melody:${k.id}:${slot}`,
        label: custom || `${MELODY_LABELS[slot] || slot} · ${k.name || "Kit"}`,
        url: toAbsUrl(meta.url),
        kit: "melody", kitId: k.id, kitName: k.name, slot,
        rootPitch: root, oneShot: false,
      });
    });
  });
  return sounds;
}

// Module-level state — one canonical snapshot shared across every
// Sampler instance in the tree.
let _snapshot = [];
let _hasFetched = false;
let _inFlight = null;
// v19.7 (iter47 RCA fix) — pulse dirty flag. If a kitsBus pulse arrives
// mid-flight (e.g. producer deletes a slot while a rename refresh is
// still fetching), the coalesced return would deliver a snapshot taken
// BEFORE the mutation and no follow-up would ever run — the DAW could
// stay stale indefinitely. We now set `_pendingRefresh` when a pulse
// arrives during an in-flight fetch, and re-run refresh once the
// current one completes.
let _pendingRefresh = false;
const _subscribers = new Set();

function _emit() {
  for (const fn of _subscribers) {
    try { fn(_snapshot); } catch (e) { /* swallow subscriber errors */ }
  }
}

// Kick off a full library refresh. Coalesces concurrent calls so a
// pulse-storm (add + rename + delete in rapid succession) doesn't
// spawn three overlapping fetches. Returns the fresh snapshot.
//
// v19.7 (iter47 RCA fix) — Previously we awaited drum/bass/melody
// kitManager.refresh(true) inside the same Promise.all as the three
// /api/training/*/all JSON fetches. Those manager refreshes call
// Tone.start() and download+decode every default-kit WAV via
// Tone.Player, so the snapshot emit was gated on 12-25s of audio
// loading. That made the DAW's Sampler dropdown appear empty for
// 12-15s on mount, and every Training-Ground pulse took the same
// window to reach the DAW.
//
// The fix: build + emit the catalog RIGHT AFTER the three lightweight
// JSON fetches (all < 500ms), then fire the three manager refreshes
// as non-awaited side effects so the audio-buffer loading happens in
// the background and doesn't block subscribers.
export async function refreshKitSoundLibrary() {
  if (_inFlight) {
    // A refresh is already in flight — mark the world as dirty so
    // we run again the moment it lands, then hand the caller the
    // existing promise so we don't spawn a second concurrent fetch.
    _pendingRefresh = true;
    return _inFlight;
  }
  _inFlight = (async () => {
    try {
      // 1. Fetch the three kit catalogs in parallel — these are the
      //    only requests that gate the snapshot.
      const [drumKits, bassKits, melodyKits] = await Promise.all([
        fetchAllKits("kits/all"),
        fetchAllKits("bass-kits/all"),
        fetchAllKits("melody-kits/all"),
      ]);
      _snapshot = buildCatalog(drumKits, bassKits, melodyKits);
      _hasFetched = true;
      _emit();
      // 2. Fire manager refreshes in the background (audio buffers).
      //    Errors are swallowed — the catalog is already published.
      drumKitManager.refresh(true).catch(() => {});
      bassKitManager.refresh(true).catch(() => {});
      melodyKitManager.refresh(true).catch(() => {});
      return _snapshot;
    } finally {
      _inFlight = null;
      // If any pulse arrived during our fetch, run once more so the
      // catalog reflects the latest state.
      if (_pendingRefresh) {
        _pendingRefresh = false;
        refreshKitSoundLibrary();
      }
    }
  })();
  return _inFlight;
}

// Subscribe. Callback fires immediately with the current snapshot IF a
// fetch has already happened (so late-mounting Samplers don't miss the
// initial catalog). Returns an unsubscribe fn.
export function subscribeKitSoundLibrary(fn) {
  _subscribers.add(fn);
  if (_hasFetched) {
    try { fn(_snapshot); } catch (e) { /* noop */ }
  } else {
    // Auto-kick the first fetch so subscribers don't need to.
    refreshKitSoundLibrary();
  }
  return () => { _subscribers.delete(fn); };
}

// Backwards-compatible one-shot getter for callers that just want a
// promise of the current library (e.g. the DAWPage reconcile effect).
// Always resolves to a *fresh* snapshot when the caller cares about
// deletion detection.
export async function getKitSoundLibrary({ forceFresh = true } = {}) {
  if (forceFresh || !_hasFetched) {
    await refreshKitSoundLibrary();
  }
  return _snapshot;
}

// Wire the kitsBus pulse ONCE at module import — every Training-Ground
// CRUD op (add / delete / rename / kit-delete / set-default) now
// triggers a single central refresh that fans out to every subscriber.
onKitsChanged(() => { refreshKitSoundLibrary(); });
